import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { Payment } from '@ankorstore/api-internal/Payment';
import usePrice from '@core/composables/use-price';
import type { Amount } from '@core/types/amount';
import { captureConvenienceException } from '@client/monolith/core/plugins/sentry/helper';
import { isEnabled, FeatureFlag } from '@monolith/legacy/services/features';
import { convertFromCents } from '@modules/core/services/price/price';
import { clearHeaderPillsCache, getCmsHeaderPills } from '@monolith/legacy/modules/promote-engage/services/pill';
import {
  CreditLimitCmsStatus,
  ARIA_PSP_NAME,
  LEGAL_INFORMATION_PROTECTED_REASON,
  KYC_REQUIRED_REASON,
  KYC_IN_REVIEW_REASON,
} from './credit-limit-state';

interface RetailerAvailableCreditResponse {
  data?: {
    type?: string;
    id?: string;
    attributes?: {
      orders?: { amount: number; currency: string };
    };
  };
}

// Pill-owned singleton refs (CMS data)
const isPillLoading = ref(false);
const availableAmount = ref<{ value: number; currency: string }>({ value: 0, currency: 'EUR' });
const pspName = ref<string | null>(null);
const cmsStatus = ref<string | null>(null);
const refusalReasons = ref<string[] | null>(null);
const unpaidAmount = ref<{ value: number; currency: string }>({ value: 0, currency: 'EUR' });
const activeProviderCount = ref<number>(1);

// Client-only flag flipped after BI save to hide the pill while backend catches up to the new status.
// Gates every Aria-specific badge and showCreditLimitPill; cleared when a fresh fetch applies a non-refused status.
const isOptimisticallyProcessing = ref(false);

// Promise-based cache to avoid duplicate concurrent requests
let fetchPromise: Promise<void> | null = null;

export function clearPillState(): void {
  fetchPromise = null;
  isPillLoading.value = false;
  availableAmount.value = { value: 0, currency: 'EUR' };
  pspName.value = null;
  cmsStatus.value = null;
  refusalReasons.value = null;
  unpaidAmount.value = { value: 0, currency: 'EUR' };
  activeProviderCount.value = 1;
  isOptimisticallyProcessing.value = false;
}

// Module-level helpers — safe to call outside setup / after awaits (do not touch useStore/inject).
export function setPillOptimisticProcessing(): void {
  isOptimisticallyProcessing.value = true;
}

export function clearPillOptimisticProcessing(): void {
  isOptimisticallyProcessing.value = false;
}

export function getPillCmsStatus(): string | null {
  return cmsStatus.value;
}

export function applyCmsCreditLimitToPill(creditLimit?: {
  amount: number;
  currency: string;
  pspName?: string;
  status?: string;
  refusalReasons?: string[] | null;
  unpaidTotalAmount?: number | null;
  unpaidTotalCurrency?: string | null;
  activeProviderCount?: number;
}): void {
  if (!creditLimit) {
    return;
  }
  availableAmount.value = {
    value: convertFromCents(creditLimit.amount),
    currency: creditLimit.currency || 'EUR',
  };
  pspName.value = creditLimit.pspName ?? null;
  cmsStatus.value = creditLimit.status ?? null;
  refusalReasons.value = creditLimit.refusalReasons ?? null;
  activeProviderCount.value = creditLimit.activeProviderCount ?? 1;
  if (creditLimit.unpaidTotalAmount != null) {
    unpaidAmount.value = {
      value: creditLimit.unpaidTotalAmount,
      currency: creditLimit.unpaidTotalCurrency || 'EUR',
    };
  }
}

/**
 * CMS path composable — serves header pill, banner, cart/checkout.
 * Data comes from CMS header-pills response or lightweight Payment API fallback.
 */
export function useCreditLimitPill() {
  const store = useStore();
  const { formatPrice } = usePrice();

  const retailerUuid = computed(() => store.getters.retailer?.uuid);

  // CMS responded with pspName — amount 0 means all deferred payment access denied (AKS + Aria)
  const isDeniedCreditAccess = computed(() => pspName.value !== null && availableAmount.value.value === 0);

  // Gate Aria-specific UI behind country rollout flag
  const isAriaCountryEnabled = computed(() => isEnabled(FeatureFlag.CreditLimitPillNavbar));

  // Show pill when Aria flag enabled and CMS returned credit > 0, or when refused with actionable reason.
  // Hidden while an optimistic post-BI-save poll is in flight — user just resubmitted, show nothing until backend catches up.
  // TODO: Remove flag gating when pill is ready for all PSPs
  const showCreditLimitPill = computed(
    () =>
      !isOptimisticallyProcessing.value &&
      isAriaCountryEnabled.value &&
      (availableAmount.value.value > 0 ||
        creditLimitRefused.value ||
        creditLimitKycRequired.value ||
        creditLimitKycInReview.value)
  );

  // Aria KYC — neither state has a dedicated top-level CMS status: required rides on 'refused' and
  // in-review rides on 'incomplete', disambiguated by refusalReasons.
  // The FE can't verify the kycLink Aria reports is valid, so disabling this falls back to the
  // generic refused/incomplete pill below (which already excludes on these two being false) rather
  // than risk pointing the retailer at a broken link.
  const creditLimitKycRequired = computed(
    () =>
      !isOptimisticallyProcessing.value &&
      isAriaCountryEnabled.value &&
      !isEnabled(FeatureFlag.DisableAriaKycRefusal) &&
      pspName.value?.toLowerCase() === ARIA_PSP_NAME &&
      cmsStatus.value === CreditLimitCmsStatus.REFUSED &&
      (refusalReasons.value?.includes(KYC_REQUIRED_REASON) ?? false)
  );

  const creditLimitKycInReview = computed(
    () =>
      !isOptimisticallyProcessing.value &&
      isAriaCountryEnabled.value &&
      !isEnabled(FeatureFlag.DisableAriaKycRefusal) &&
      pspName.value?.toLowerCase() === ARIA_PSP_NAME &&
      cmsStatus.value === CreditLimitCmsStatus.INCOMPLETE &&
      (refusalReasons.value?.includes(KYC_IN_REVIEW_REASON) ?? false)
  );

  // CMS status 'incomplete' applies to any PSP (Aria IBAN missing, AKS mandate missing).
  // KYC in-review also rides on 'incomplete' (disambiguated above) but gets its own pill/card.
  const creditLimitIncomplete = computed(
    () =>
      !isOptimisticallyProcessing.value &&
      isAriaCountryEnabled.value &&
      !isDeniedCreditAccess.value &&
      cmsStatus.value === CreditLimitCmsStatus.INCOMPLETE &&
      !creditLimitKycInReview.value
  );

  // Aria refused — CMS returns 'refused' status when debtor not found or closed.
  // KYC required also rides on 'refused' (disambiguated above) but gets its own pill/card.
  const creditLimitRefused = computed(
    () =>
      !isOptimisticallyProcessing.value &&
      isAriaCountryEnabled.value &&
      pspName.value?.toLowerCase() === ARIA_PSP_NAME &&
      cmsStatus.value === CreditLimitCmsStatus.REFUSED &&
      !creditLimitKycRequired.value
  );

  // Refused specifically because the retailer's legal information is not public — the header-pills
  // feed lists it among the refusal reasons.
  const isLegalInformationProtectedRefusal = computed(
    () => creditLimitRefused.value && (refusalReasons.value?.includes(LEGAL_INFORMATION_PROTECTED_REASON) ?? false)
  );

  const creditLimitFrozen = computed(
    () =>
      !isOptimisticallyProcessing.value &&
      isAriaCountryEnabled.value &&
      !isDeniedCreditAccess.value &&
      pspName.value?.toLowerCase() === ARIA_PSP_NAME &&
      cmsStatus.value === CreditLimitCmsStatus.FROZEN
  );

  const hasMultipleProviders = computed(() => activeProviderCount.value > 1);

  // True when the primary PSP is Aria (used by banner to gate Aria-specific actions)
  const isAriaPsp = computed(() => pspName.value?.toLowerCase() === ARIA_PSP_NAME);

  // Floor to whole units to avoid exceeding actual credit limit due to floating-point precision
  const formattedAvailableAmount = computed(() =>
    formatPrice(
      { amount: Math.floor(availableAmount.value.value) * 100, currency: availableAmount.value.currency as Amount['currency'] },
      '$0,0'
    )
  );

  // Format unpaid amount for frozen credit displays (pill, banner, settings card)
  const formattedUnpaidAmount = computed(() =>
    unpaidAmount.value.value > 0
      ? formatPrice({ amount: unpaidAmount.value.value, currency: unpaidAmount.value.currency as Amount['currency'] }, '$0,0')
      : ''
  );

  /** Set credit limit from CMS header-pills response, avoiding a separate Payment API call. */
  const setCreditLimitFromCms = (creditLimit?: Parameters<typeof applyCmsCreditLimitToPill>[0]): void => {
    applyCmsCreditLimitToPill(creditLimit);
    if (fetchPromise === null) {
      fetchPromise = Promise.resolve();
    }
    isPillLoading.value = false;
  };

  /** Re-fetch CMS header pills and re-apply to pill state. Invalidates the module-level cache. */
  const refreshFromCms = async (): Promise<void> => {
    isPillLoading.value = true;
    clearHeaderPillsCache();
    try {
      const cmsData = await getCmsHeaderPills();
      setCreditLimitFromCms(cmsData?.credit_limit);
    } finally {
      isPillLoading.value = false;
    }
  };

  /** Lightweight fetch for header pill — uses monolith credit limit endpoint */
  const fetchCreditLimit = async (): Promise<void> => {
    if (!retailerUuid.value) {
      return;
    }

    // Return existing promise if fetch is already in progress or completed
    if (fetchPromise !== null) {
      return fetchPromise;
    }

    isPillLoading.value = true;

    const promise = (async () => {
      try {
        const paymentService = new Payment();
        const response = (await paymentService.paymentViewRetailerAvailableCredit(
          retailerUuid.value
        )) as RetailerAvailableCreditResponse;
        const data = response.data;

        if (data?.attributes?.orders) {
          const ordersAmountInEuros = convertFromCents(data.attributes.orders.amount);
          const currency = data.attributes.orders.currency || 'EUR';
          availableAmount.value = { value: ordersAmountInEuros, currency };
        }
      } catch (error) {
        captureConvenienceException(error);
      } finally {
        isPillLoading.value = false;
      }
    })();

    fetchPromise = promise;
    return promise;
  };

  return {
    // State
    isLoading: isPillLoading,
    availableAmount,
    pspName,
    // Computeds
    showCreditLimitPill,
    creditLimitIncomplete,
    creditLimitFrozen,
    creditLimitRefused,
    creditLimitKycRequired,
    creditLimitKycInReview,
    isLegalInformationProtectedRefusal,
    hasMultipleProviders,
    activeProviderCount,
    isDeniedCreditAccess,
    isAriaCountryEnabled,
    isAriaPsp,
    cmsStatus,
    // Formatters
    formattedAvailableAmount,
    formattedUnpaidAmount,
    // Optimistic state
    isOptimisticallyProcessing,
    // Actions
    setCreditLimitFromCms,
    fetchCreditLimit,
    refreshFromCms,
    setOptimisticProcessing: setPillOptimisticProcessing,
    clearOptimisticProcessing: clearPillOptimisticProcessing,
  };
}
